// src/components/pages/CategoryCityLinks.tsx
//
// Bloque de links internos para SEO.
// Muestra las top 6 categorías (por nº de empresas) con sus top 5 ciudades.
// Layout: 3 columnas. Sin llamadas extra a Supabase.

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';

interface Empresa {
  categoria_slug: string;
  ciudad_slug: string;
  categoria?: string;
  ciudad?: string;
}

interface CategoryCityLinksProps {
  empresas: Empresa[];
}

const TOP_CATEGORIAS = 6;
const TOP_CIUDADES = 5;

const CategoryCityLinks = ({ empresas }: CategoryCityLinksProps) => {
  if (!empresas?.length) return null;

  // Contar empresas por categoría
  const contadorCat: Record<string, { label: string; count: number }> = {};
  empresas.forEach((e) => {
    if (!contadorCat[e.categoria_slug]) {
      contadorCat[e.categoria_slug] = {
        label:
          e.categoria ??
          e.categoria_slug
            .replace(/-/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase()),
        count: 0,
      };
    }
    contadorCat[e.categoria_slug].count++;
  });

  // Top 6 categorías por nº de empresas
  const topCategorias = Object.entries(contadorCat)
    .sort((a, b) => b[1].count - a[1].count)
    .slice(0, TOP_CATEGORIAS)
    .map(([slug, { label }]) => ({ slug, label }));

  // Para cada categoría top, obtener top 5 ciudades por nº de empresas
  const bloques = topCategorias.map(({ slug: catSlug, label: catLabel }) => {
    const contadorCiudad: Record<string, { label: string; count: number }> = {};

    empresas
      .filter((e) => e.categoria_slug === catSlug)
      .forEach((e) => {
        if (!contadorCiudad[e.ciudad_slug]) {
          contadorCiudad[e.ciudad_slug] = {
            label:
              e.ciudad ??
              e.ciudad_slug
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase()),
            count: 0,
          };
        }
        contadorCiudad[e.ciudad_slug].count++;
      });

    const topCiudades = Object.entries(contadorCiudad)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, TOP_CIUDADES)
      .map(([ciudadSlug, { label }]) => ({ ciudadSlug, label }));

    return { catSlug, catLabel, ciudades: topCiudades };
  });

  if (!bloques.length) return null;

  return (
    <section className="w-full bg-white dark:bg-black py-16 px-4 sm:px-0">
      <div className="w-full sm:px-[15%]">
        {/* Cabecera */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <h2 className="text-3xl sm:text-4xl font-semibold text-[#103254] dark:text-white">
            Descubre negocios en tu ciudad
          </h2>
          <div className="w-12 h-px mt-4 bg-[#288E7F]" />
        </motion.div>

        {/* Grid 3 columnas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {bloques.map(({ catSlug, catLabel, ciudades }, i) => (
            <motion.div
              key={catSlug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.07 }}
              className="bg-gray-50 dark:bg-[#1c1c1e] rounded-2xl p-5 border border-gray-100 dark:border-white/10"
            >
              {/* Título categoría */}
              <h3 className="text-sm font-bold text-[#103254] dark:text-white mb-4 leading-tight">
                {catLabel}
              </h3>

              {/* Lista ciudades */}
              <ul className="space-y-2">
                {ciudades.map(({ ciudadSlug, label }) => (
                  <li key={ciudadSlug}>
                    <Link
                      to={`/${catSlug}/${ciudadSlug}`}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-[#103254] dark:hover:text-white transition-colors duration-200 group"
                    >
                      <MapPin className="w-3.5 h-3.5 shrink-0 text-[#288E7F] group-hover:text-[#103254] dark:group-hover:text-white transition-colors" />
                      <span className="group-hover:underline underline-offset-2">
                        {label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryCityLinks;